import React from "react";
import {
  ChannelPreviewUIComponentProps,
  useChatContext,
} from "stream-chat-react";
import ChannelUserAvatar from "@/components/pages/chats/component/chat-custom-components/ChannelUserAvatar";
import { getShortChatDate } from "@/utils/dateUtils";
import { Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isLastMessageRead } from "@/components/pages/chats/component/chat-custom-components/messageReadStatus";
import { isGroupChannel } from "@/components/pages/chats/utils/channel-mentions";
import { cn } from "@/lib/utils";

export const InternalCustomListItem = (
  props: ChannelPreviewUIComponentProps,
) => {
  const { t } = useTranslation();
  const { client } = useChatContext();
  const { channel, active, lastMessage, unread, setActiveChannel } = props;

  const currentUserId = client.userID;

  const isGroup = isGroupChannel(channel);

  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user?.id !== currentUserId,
  )?.user;

  const activeUserName = isGroup
    ? channel.data?.name || t("chats.groupChat")
    : otherMember?.name ||
      lastMessage?.user?.name ||
      channel.data?.created_by?.name;

  const attachmentCount = lastMessage?.attachments?.length || 0;
  const isOnlyAttachmentMessage = !lastMessage?.text && attachmentCount > 0;
  const lastMessageRead = isLastMessageRead(
    channel,
    lastMessage,
    currentUserId,
  );

  const renderMessagePreview = () => {
    if (lastMessage?.text) {
      return lastMessage.text;
    }

    if (isOnlyAttachmentMessage) {
      return (
        <span className="flex items-center gap-1 font-medium text-blue-600">
          <Paperclip className="size-3" />
          {attachmentCount}{" "}
          {attachmentCount === 1
            ? t("chats.attachment")
            : t("chats.attachments")}{" "}
          {t("chats.sent")}
        </span>
      );
    }

    return (
      <span className="text-slate-400 italic">{t("chats.emptyChat")}</span>
    );
  };

  const hasUnread = Boolean(unread && unread > 0);
  const handleChannelSelect = () => setActiveChannel?.(channel);

  return (
    <button
      type="button"
      onClick={handleChannelSelect}
      className={cn(
        "mx-2 mb-2 flex w-[calc(100%-1rem)] flex-col gap-2 rounded-lg border p-2 text-left transition-colors",
        active
          ? "border-royal-glow-3 bg-royal-glow-5 border-2"
          : "border-neutral-200 bg-neutral-50 hover:bg-neutral-100",
      )}
    >
      <div className="flex w-full flex-row items-center gap-2">
        <div className="relative shrink-0">
          <ChannelUserAvatar
            name={activeUserName}
            isGroup={isGroup}
            className="size-9"
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-row items-center gap-4">
            <span className="text-eathy-comfort-1 min-w-0 flex-1 truncate text-xs font-medium">
              {activeUserName}
            </span>
            <div className="flex shrink-0 flex-row items-center gap-1.5">
              {lastMessageRead ? (
                <span
                  className="text-monochrome-core-2 text-[10px] font-semibold"
                  aria-label={t("chats.read")}
                  title={t("chats.read")}
                >
                  ✓✓
                </span>
              ) : null}
              {lastMessage?.updated_at ? (
                <span className="text-xs font-light whitespace-nowrap text-neutral-900/75">
                  {getShortChatDate(lastMessage.updated_at)}
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-row items-end gap-1">
            <p className="line-clamp-2 min-w-0 flex-1 text-xs break-words text-neutral-900">
              {renderMessagePreview()}
            </p>
            {hasUnread ? (
              <span className="bg-royal-glow-3 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
                {unread}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
};
